import { useState, useMemo } from "react";

import { countryTextByIdFilter } from "app/filters/ngFilters";

import type { CountryDataItem } from "../types";
import { sortByCountryOrder } from "../utils/chartUtils";
import { processTableContainerData } from "../utils/dataProcessors";

import { useGeographyTableSingleData } from "./geographyQueries";

/**
 * Hook that provides both raw and processed geography table data.
 * This ensures the table and chart components use the same sorted/filtered data.
 *
 * Returns both filtered and original country order references:
 * - `orderedTopCountryIds`: Current top countries (affected by search filtering)
 * - `originalOrderedCountryIds`: Original top countries (stable, unfiltered) - used for consistent legend ordering
 *
 * The `originalOrderedCountryIds` prevents legend shuffling during table search operations
 * by maintaining a stable reference to the original table order.
 */
export const useProcessedGeographyTableData = () => {
    const { data: rawTableData, isLoading, isError } = useGeographyTableSingleData();

    // Local state for table interactions (should be controlled by parent)
    const [searchString, setSearchString] = useState("");
    const [sortedColumn, setSortedColumn] = useState({
        field: "TrafficShare",
        sortDirection: "desc" as "desc" | "asc",
    });

    // Table configuration constants
    const tableSelectionProperty = "Country";

    // Original unfiltered data order (stable reference for legend consistency)
    const originalOrderedCountryIds = useMemo(() => {
        if (!rawTableData?.Data) {
            return [];
        }

        // Process data without search filtering to get stable original order
        // This ensures legend ordering remains consistent during table search operations
        return processTableContainerData(
            rawTableData,
            "", // No search filter - preserves original table order
            sortedColumn,
            tableSelectionProperty,
            countryTextByIdFilter,
        )
            .slice(0, 10)
            .map((item) => item.Country);
    }, [rawTableData, sortedColumn]);

    const processedData = useMemo(() => {
        if (!rawTableData?.Data) {
            return [];
        }

        return processTableContainerData(
            rawTableData,
            searchString,
            sortedColumn,
            tableSelectionProperty,
            countryTextByIdFilter,
        );
    }, [rawTableData, searchString, sortedColumn]);

    const orderedTopCountryIds = useMemo(() => {
        return processedData.slice(0, 10).map((item) => item.Country);
    }, [processedData]);

    return {
        // Raw data
        rawTableData,
        isLoading,
        isError,

        // Processed data
        processedData,
        orderedTopCountryIds, // Current top countries (filtered by search)
        originalOrderedCountryIds, // Original top countries (stable, unfiltered) - used for consistent legend ordering

        // Table controls
        searchString,
        setSearchString,
        sortedColumn,
        setSortedColumn,

        // Helper methods
        onSearch: (val: string) => {
            setSearchString(val.trim().toLowerCase());
        },
        onSort: ({ field, sortDirection }: { field: string; sortDirection: "desc" | "asc" }) => {
            setSortedColumn({ field, sortDirection });
        },
    };
};

/**
 * Helper to get selected countries ordered by their position in the processed table data
 * This ensures selected countries are shown in table order (highest to lowest value)
 */
const getSelectedCountriesInTableOrder = <T extends { Country: number }>(
    selectedCountries: T[],
    processedTableData: CountryDataItem[],
): T[] => {
    if (!selectedCountries?.length || !processedTableData?.length) {
        return selectedCountries || [];
    }

    const countriesOrder = processedTableData.map((row) => row.Country);
    return sortByCountryOrder(selectedCountries, countriesOrder);
};
